/**
 * Widget Skeleton Component — inline loading placeholder.
 *
 * Shown only while the inline widget is in `WidgetState.Loading`, before any
 * market is known, filling the slot the inline mount already reserves
 * (`widgetSurfaceMinHeight[defaultView]`) so the partner article shows a
 * shimmering placeholder instead of a blank gap. It renders no market data —
 * hence "Widget" rather than the `Market*` surfaces' prefix.
 *
 * The shape mirrors the configured default view (`data-default-view`): pill,
 * preview, or card. No Figma frame exists for this surface — each variant
 * shares its real surface's shell (`pill/preview/cardSurfaceShellClasses`) so
 * the hand-off to the loaded surface is seamless. The only un-designed element
 * is the generic shimmer sweep; the inner blocks are a representative stand-in
 * for each surface's structure, not a pixel-perfect replica.
 *
 * Decorative and non-interactive: `aria-hidden` removes it from the a11y tree
 * and `pointer-events-none` lets clicks pass through. The sweep is pure CSS
 * (no JS timers) and is disabled under `prefers-reduced-motion`.
 */

import type { ComponentChildren, JSX } from "preact";
import { getConfig } from "../config";
import { WidgetView } from "../types";
import {
  cardSurfaceShellClasses,
  floatingPositionClassByPosition,
  inlineTrailingSpaceClass,
  pillSurfaceShellClasses,
  previewSurfaceShellClasses,
  WidgetPosition,
  widgetSurfaceWidthClass,
} from "../utils/constants";
import { AdvertisementLabel } from "./advertisement-label";

const blockClass = "pw:bg-type-muted-foreground";

const shellClassByView: Record<WidgetView, string> = {
  [WidgetView.Pill]: pillSurfaceShellClasses,
  [WidgetView.Preview]: previewSurfaceShellClasses,
  [WidgetView.Card]: cardSurfaceShellClasses,
};

export function WidgetSkeleton(): JSX.Element {
  const view = getConfig().defaultView;
  return (
    <SkeletonShell view={view}>
      {view === WidgetView.Pill ? (
        <PillBlocks />
      ) : view === WidgetView.Card ? (
        <CardBlocks />
      ) : (
        <PreviewBlocks />
      )}
    </SkeletonShell>
  );
}

// Shared shell: the loaded surface's chrome plus a containing block for the
// absolutely positioned sweep (`pw:relative pw:overflow-hidden` clips it to the
// rounded shape). `pw:pointer-events-none` lets clicks pass through.
//
// The outer wrapper mirrors the real surfaces' structure — position and width
// outside, chrome inside — so the AdvertisementLabel can sit above the clipped
// shell. Without the label here the inline slot would shrink by its height the
// moment the real surface replaced the skeleton, jumping the partner article.
// `aria-hidden` on the wrapper covers the label too: the skeleton is a
// placeholder, and the real surface carries the actual disclosure.
function SkeletonShell({
  view,
  children,
}: {
  view: WidgetView;
  children: ComponentChildren;
}): JSX.Element {
  const wrapperClassName = [
    // The skeleton only ever renders inline; mirror the real surfaces' inline
    // layout (`pw:block pw:mx-auto`). Every inline surface is `w-full` today,
    // so the centring is inert — it is kept in step with the real surfaces so
    // a future `w-max` one centres in the host's slot rather than sitting left.
    floatingPositionClassByPosition[WidgetPosition.Inline],
    widgetSurfaceWidthClass(WidgetPosition.Inline, view),
    inlineTrailingSpaceClass,
    "pw:font-sans",
    "pw:pointer-events-none",
  ].join(" ");

  const shellClassName = [
    shellClassByView[view],
    "pw:w-full",
    "pw:relative pw:overflow-hidden",
  ].join(" ");

  return (
    <div aria-hidden="true" className={wrapperClassName}>
      <SkeletonAdvertisementLabel view={view} />
      <div className={shellClassName}>
        {children}
        <Shimmer />
      </div>
    </div>
  );
}

// The label's own band height is fixed, so only the preview needs anything
// extra — a stand-in for the close button it carries in the row. The card's
// close control lives inside its own chrome, so its band is the plain label.
function SkeletonAdvertisementLabel({
  view,
}: {
  view: WidgetView;
}): JSX.Element {
  if (view === WidgetView.Preview) {
    return (
      <AdvertisementLabel
        trailing={
          <div className={`pw:h-6 pw:w-6 pw:rounded-full ${blockClass}`} />
        }
      />
    );
  }
  return <AdvertisementLabel />;
}

// A translucent highlight band that travels across the surface. The gradient
// track is twice the width and we animate its `background-position`, so the
// highlight stays on-screen throughout.
function Shimmer(): JSX.Element {
  return (
    <div className="pw:absolute pw:inset-0 pw:animate-shimmer pw:motion-reduce:animate-none pw:bg-[length:200%_100%] pw:bg-[linear-gradient(110deg,transparent_40%,rgba(255,255,255,0.14)_50%,transparent_60%)]" />
  );
}

// Mirrors the Pill's brand mark + CTA text. The skeleton only ever renders
// inline, so the mark matches the inline pill's 55px rather than the floating
// pill's 40px.
function PillBlocks(): JSX.Element {
  return (
    <>
      <div
        className={`pw:h-[3.4375rem] pw:w-[3.4375rem] pw:flex-shrink-0 pw:rounded-full ${blockClass}`}
      />
      <div className={`pw:h-3 pw:w-28 pw:rounded ${blockClass}`} />
    </>
  );
}

// Mirrors the Preview left group (image + title) and the action pill.
function PreviewBlocks(): JSX.Element {
  return (
    <>
      <div className="pw:flex pw:items-center pw:gap-2 pw:flex-1 pw:min-w-0 pw:h-full">
        <div
          className={`pw:h-16 pw:w-16 pw:flex-shrink-0 pw:rounded-full ${blockClass}`}
        />
        <div className="pw:flex pw:flex-col pw:gap-2 pw:flex-1 pw:min-w-0">
          <div className={`pw:h-3 pw:w-3/4 pw:rounded ${blockClass}`} />
          <div className={`pw:h-3 pw:w-1/2 pw:rounded ${blockClass}`} />
        </div>
      </div>
      <div
        className={`pw:h-12 pw:w-20 pw:flex-shrink-0 pw:rounded-full ${blockClass}`}
      />
    </>
  );
}

// Representative stand-in for the Card's header / content / outcomes / CTA /
// footer regions. Vertical rhythm mirrors `market-card.tsx` (mt 5/6/8/4).
function CardBlocks(): JSX.Element {
  return (
    <>
      {/* Brand header: mark + label, close button. */}
      <div className="pw:flex pw:items-center pw:justify-between pw:w-full">
        <div className="pw:flex pw:items-center pw:gap-4">
          <div className={`pw:h-7 pw:w-7 pw:rounded-full ${blockClass}`} />
          <div className={`pw:h-3 pw:w-24 pw:rounded ${blockClass}`} />
        </div>
        <div className={`pw:h-6 pw:w-6 pw:rounded-full ${blockClass}`} />
      </div>

      {/* Content block: image + title lines, then the time-left pill. */}
      <div className="pw:flex pw:flex-col pw:gap-4 pw:w-full pw:mt-5">
        <div className="pw:flex pw:items-center pw:gap-2 pw:w-full">
          <div
            className={`pw:h-[3.625rem] pw:w-[3.625rem] pw:flex-shrink-0 pw:rounded-2xl ${blockClass}`}
          />
          <div className="pw:flex pw:flex-col pw:gap-2 pw:flex-1 pw:min-w-0">
            <div className={`pw:h-4 pw:w-3/4 pw:rounded ${blockClass}`} />
            <div className={`pw:h-4 pw:w-1/2 pw:rounded ${blockClass}`} />
          </div>
        </div>
        <div className={`pw:h-6 pw:w-28 pw:rounded-3xl ${blockClass}`} />
      </div>

      {/* Outcome list: two representative rows. */}
      <div className="pw:flex pw:flex-col pw:gap-3 pw:w-full pw:mt-6">
        <div className={`pw:h-12 pw:w-full pw:rounded-xl ${blockClass}`} />
        <div className={`pw:h-12 pw:w-full pw:rounded-xl ${blockClass}`} />
      </div>

      {/* CTA bar. */}
      <div
        className={`pw:h-10 pw:w-full pw:rounded-full pw:mt-8 ${blockClass}`}
      />

      {/* Footer: logo lockup + compliance pill. */}
      <div className="pw:flex pw:items-center pw:justify-between pw:w-full pw:mt-4 pw:pt-2">
        <div
          className={`pw:h-[1.6rem] pw:w-[8.8125rem] pw:rounded ${blockClass}`}
        />
        <div className={`pw:h-7 pw:w-16 pw:rounded-full ${blockClass}`} />
      </div>

      {/* Closing advertisement-source disclosure — two 16px lines at every card
          width below ~500px, which is every inline column in practice. */}
      <div className="pw:flex pw:flex-col pw:items-center pw:w-full pw:mt-4">
        <div className={`pw:h-4 pw:w-3/4 pw:rounded ${blockClass}`} />
        <div className={`pw:h-4 pw:w-1/2 pw:rounded ${blockClass}`} />
      </div>
    </>
  );
}
